<template>
  <div class="calendar-wrapper">
    <!-- Calendar View -->
    <div
      v-if="currentView === 'calendar'"
      class="calendar-timeline"
      :style="{ '--calendar-sticky-top': stickyTopOffset + 'px' }"
    >
      <!-- Sticky header з view-toggle ОДИН РАЗ для всіх днів -->
      <div class="calendar-sticky-header" v-if="currentStickyDay">
        <div class="calendar-sticky-header__left">
          <h3>{{ currentStickyDay }}</h3>
        </div>

        <!-- VIEW TOGGLE BUTTON -->
        <view-toggle
          v-if="viewTabs && viewTabs.length > 0"
          :tabs="viewTabs"
          :current-view="currentView"
          :hide-label-on-mobile="true"
          @view-changed="switchView"
        />
      </div>

      <div
        v-for="(day, index) in calendarDays"
        :key="day.id"
        class="calendar-day-section"
        :class="{ 'first-day': index === 0 }"
      >
        <div class="calendar-day-title">
          <h3>{{ day.date_long }}</h3>
        </div>

        <div class="timeline-container">
          <!-- Time labels -->
          <div class="time-labels">
            <div
              v-for="hour in day.hours"
              :key="hour"
              class="time-label"
            >
              {{ formatHour(hour) }}
            </div>
          </div>

          <!-- Timeline grid -->
          <div class="timeline-grid" :style="{ height: day.totalGridHeight + 'px' }">
            <!-- Hour lines (horizontal) -->
            <div
              v-for="(hour, index) in day.hours"
              :key="`line-${hour}`"
              class="hour-line"
              :style="{ top: `calc(${index} * 60px)` }"
            ></div>

            <!-- Session blocks -->
            <div
              v-for="session in day.registeredSessions"
              :key="session.id"
              class="session-block"
              :class="{ 'background-session': session.isBackground }"
              :style="getSessionStyle(session, day)"
              @click="openSessionDetails(session)"
            >
              <div class="session-block-content">
                <div class="session-name">{{ session.name }}</div>
              </div>

              <!-- Tooltip for full details on hover (desktop only) -->
              <div
                class="session-tooltip"
                :class="{
                  'tooltip-top': isBottomHalf(session, day),
                  'tooltip-right': isRightSide(session, day)
                }"
              >
                <div class="tooltip-header">
                  <span class="tooltip-time">
                    {{ session.start_at_formatted }} - {{ session.end_at_formatted }}
                  </span>
                </div>
                <div class="tooltip-title">{{ session.name }}</div>
                <div class="tooltip-footer" v-if="session.room_name">
                  <i class="fa fa-map-marker"></i> {{ session.room_name }}
                </div>
              </div>
            </div>

            <!-- Gap blocks (clickable free time) -->
            <div
              v-for="(gap, index) in day.gaps"
              :key="`gap-${index}`"
              class="gap-block"
              :style="getGapStyle(gap, day)"
              @click="openGapModal(gap, day)"
            >
              <div class="gap-content">
                <div class="gap-header">
                  <i class="fa fa-plus-circle"></i>
                  <span>Add Session</span>
                </div>
                <div class="gap-time">
                  {{ formatTime(gap.start) }} - {{ formatTime(gap.end) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Gap Modal -->
    <gap-sessions-modal
      :is-open="showGapModal"
      :selected-gap="selectedGap"
      :available-sessions="availableSessions"
      :attendee-id="attendeeId"
      @close="closeGapModal"
      @session-updated="handleSessionUpdated"
    />
  </div>
</template>

<script>
import ViewToggle from "../../../components/ui/ViewToggle/index";
import GapSessionsModal from "./gap-sessions-modal";

export default {
  name: 'CalendarView',
  components: {
    ViewToggle,
    GapSessionsModal
  },
  props: {
    agenda: {
      type: Array,
      required: true
    },
    initialView: {
      type: String,
      default: 'list'
    },
    attendeeId: {
      type: [String, Number],
      required: true
    },
    viewTabs: {
      type: Array,
      default: () => [
        { value: 'list', label: 'List', icon: 'fa fa-list' },
        { value: 'calendar', label: 'Calendar', icon: 'fa fa-calendar' }
      ]
    }
  },
  mounted() {
    setTimeout(() => {
      this.calculateStickyTop();
      this.initDayObserver();
      this.observeDayHeaders();
    }, 100);

    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this._onScroll, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);

    if (this._onScroll) {
      window.removeEventListener('scroll', this._onScroll);
    }

    this.disconnectDayObserver();

    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = 0;
    }
  },
  data() {
    return {
      showGapModal: false,
      selectedGap: null,
      selectedDay: null,
      availableSessions: [],
      windowWidth: window.innerWidth,
      calculatedStickyTop: 80,

      currentStickyDay: '',
      _dayIO: null,
      _observedDayHeaders: [],
      _scrollRaf: 0,
    }
  },
  watch: {
    initialView(newView) {
      setTimeout(() => {
        this.calculateStickyTop();
        if (newView === 'calendar') {
          this.$nextTick(() => {
            this.rebuildDayObserver();
          });
        }
      }, 100);
    },

    calendarDays() {
      this.$nextTick(() => {
        this.rebuildDayObserver();
      });
    },
  },
  computed: {
    calendarDays() {
      return this.agenda.map(day => {
        if (!day.sessions || day.sessions.length === 0) {
          return null;
        }

        const registeredSessions = day.sessions.filter(s => s.my_agenda === true);

        const times = day.sessions.map(s => ({
          start: new Date(s.start_at),
          end: new Date(s.end_at)
        }));

        const startTime = new Date(Math.min(...times.map(t => t.start)));
        const endTime = new Date(Math.max(...times.map(t => t.end)));

        const startHour = startTime.getHours();
        const endHour = endTime.getHours() + (endTime.getMinutes() > 0 ? 1 : 0);

        const hours = [];
        for (let h = startHour; h <= endHour; h++) {
          hours.push(h);
        }

        const totalGridHeight = hours.length * 60;

        const gaps = this.calculateGaps(registeredSessions, startTime, endTime, day.sessions);

        // Calculate overlap levels for registered sessions
        const sessionsWithOverlap = this.calculateSessionOverlaps(registeredSessions);

        return {
          ...day,
          registeredSessions: sessionsWithOverlap,
          hours,
          startTime,
          endTime,
          gaps,
          totalGridHeight
        }
      }).filter(Boolean);
    },

    stickyTopOffset() {
      return this.calculatedStickyTop;
    },

    currentView() {
      return this.initialView;
    }
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
      this.$nextTick(() => {
        this.calculateStickyTop();
      });
    },

    calculateStickyTop() {
      const isMobile = this.windowWidth < 900;

      if (!isMobile) {
        this.calculatedStickyTop =
          this._cssVarPx('--attendee-header-height', 80) +
          this._cssVarPx('--attendee-gap-md', 0) +
          this._cssVarPx('--attendee-gap-lg', 0);
      } else {
        let top = this._cssVarPx('--attendee-header-mobile-height', 56);

        const tabsEl = document.querySelector('.scheduler-mobile-tabs');
        if (tabsEl && tabsEl.offsetParent !== null) {
          const tabsHeight = Math.round(tabsEl.getBoundingClientRect().height);
          top += tabsHeight;
        }

        const viewToggleEl = document.querySelector('.calendar-wrapper .view-toggle');
        if (viewToggleEl && viewToggleEl.offsetParent !== null) {
          const viewToggleHeight = Math.round(viewToggleEl.getBoundingClientRect().height);
          top += viewToggleHeight;

          const styles = window.getComputedStyle(viewToggleEl);
          const marginBottom = parseInt(styles.marginBottom) || 20;
          top += marginBottom;
        }

        this.calculatedStickyTop = top;
      }
    },

    _cssVarPx(name, fallback = 0) {
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return fallback;
      }
      const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? n : fallback;
    },

    switchView(view) {
      this.$emit('view-changed', view);
    },

    calculateGaps(sessions, dayStart, dayEnd, allDaySessions) {
      const gaps = [];

      if (sessions.length === 0) {
        const availableSessions = allDaySessions
          .filter(s => !s.my_agenda && s.buttons?.my_schedule?.enabled === true)
          .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

        if (availableSessions.length === 0) {
          return gaps;
        }

        let currentGapStart = new Date(availableSessions[0].start_at);
        let currentGapEnd = new Date(availableSessions[0].end_at);

        for (let i = 1; i < availableSessions.length; i++) {
          const sessionStart = new Date(availableSessions[i].start_at);
          const sessionEnd = new Date(availableSessions[i].end_at);

          if (sessionStart <= currentGapEnd) {
            currentGapEnd = new Date(Math.max(currentGapEnd, sessionEnd));
          } else {
            gaps.push({
              start: currentGapStart,
              end: currentGapEnd
            });
            currentGapStart = sessionStart;
            currentGapEnd = sessionEnd;
          }
        }

        gaps.push({
          start: currentGapStart,
          end: currentGapEnd
        });

        return gaps;
      }

      const sorted = [...sessions].sort((a, b) =>
        new Date(a.start_at) - new Date(b.start_at)
      );

      let currentTime = dayStart;

      sorted.forEach(session => {
        const sessionStart = new Date(session.start_at);

        if (sessionStart > currentTime) {
          const hasAvailableSessions = this.hasAvailableSessionsInGap(
            new Date(currentTime),
            new Date(sessionStart),
            allDaySessions
          );

          if (hasAvailableSessions) {
            gaps.push({
              start: new Date(currentTime),
              end: new Date(sessionStart)
            });
          }
        }

        const sessionEnd = new Date(session.end_at);
        currentTime = sessionEnd > currentTime ? sessionEnd : currentTime;
      });

      if (currentTime < dayEnd) {
        const hasAvailableSessions = this.hasAvailableSessionsInGap(
          new Date(currentTime),
          new Date(dayEnd),
          allDaySessions
        );

        if (hasAvailableSessions) {
          gaps.push({
            start: new Date(currentTime),
            end: new Date(dayEnd)
          });
        }
      }

      return gaps;
    },

    hasAvailableSessionsInGap(gapStart, gapEnd, allSessions) {
      return allSessions.some(session => {
        if (session.my_agenda === true) return false;
        if (!session.buttons?.my_schedule?.enabled) return false;

        const sessionStart = new Date(session.start_at);
        const sessionEnd = new Date(session.end_at);

        return sessionStart >= gapStart && sessionEnd <= gapEnd;
      });
    },

    calculateSessionOverlaps(sessions) {
      if (sessions.length === 0) return sessions;

      const sorted = [...sessions].sort((a, b) => {
        const startDiff = new Date(a.start_at) - new Date(b.start_at);
        if (startDiff !== 0) return startDiff;
        return new Date(b.end_at) - new Date(a.end_at);
      });

      // Calculate session durations in hours
      sorted.forEach(session => {
        const start = new Date(session.start_at);
        const end = new Date(session.end_at);
        session.durationHours = (end - start) / (1000 * 60 * 60);
      });

      const threshold = 2; // hours
      const backgroundSessions = sorted.filter(s => s.durationHours >= threshold);
      const regularSessions = sorted.filter(s => s.durationHours < threshold);

      // Mark background sessions
      backgroundSessions.forEach(session => {
        session.isBackground = true;
        session.column = 0;
        session.totalColumns = 1;
      });

      // Process regular sessions
      const result = [];
      let currentCluster = [];
      let clusterEnd = null;

      const processCluster = (cluster) => {
        const columns = [];

        cluster.forEach(session => {
          const start = new Date(session.start_at);
          const end = new Date(session.end_at);
          let placed = false;

          for (let i = 0; i < columns.length; i++) {
            if (columns[i] <= start) {
              columns[i] = end;
              session.column = i;
              placed = true;
              break;
            }
          }

          if (!placed) {
            columns.push(end);
            session.column = columns.length - 1;
          }
        });

        const totalColumns = columns.length;
        cluster.forEach(session => {
          session.totalColumns = totalColumns;
          session.isBackground = false;
          result.push(session);
        });
      };

      regularSessions.forEach(session => {
        const start = new Date(session.start_at);
        const end = new Date(session.end_at);

        if (currentCluster.length === 0) {
          currentCluster.push(session);
          clusterEnd = end;
        } else {
          if (start < clusterEnd) {
            currentCluster.push(session);
            if (end > clusterEnd) {
              clusterEnd = end;
            }
          } else {
            processCluster(currentCluster);
            currentCluster = [session];
            clusterEnd = end;
          }
        }
      });

      if (currentCluster.length > 0) {
        processCluster(currentCluster);
      }

      // Return background sessions first, then regular
      return [...backgroundSessions, ...result];
    },

    getSessionStyle(session, day) {
      const sessionStart = new Date(session.start_at);
      const sessionEnd = new Date(session.end_at);

      const firstHour = day.hours[0];
      const gridStartTime = new Date(day.startTime);
      gridStartTime.setHours(firstHour, 0, 0, 0);

      const gridStartMs = gridStartTime.getTime();

      const startOffsetMs = sessionStart.getTime() - gridStartMs;
      const durationMs = sessionEnd.getTime() - sessionStart.getTime();

      const startOffsetHours = startOffsetMs / (1000 * 60 * 60);
      const durationHours = durationMs / (1000 * 60 * 60);

      const topPx = startOffsetHours * 60;
      const heightPx = durationHours * 60;

      const currentColumn = session.column || 0;
      const isMobile = this.windowWidth < 900;
      const isBackground = session.isBackground || false;

      let leftStyle, widthStyle, zIndex;

      if (isMobile) {
        if (isBackground) {
          leftStyle = '0%';
          widthStyle = '100%';
          zIndex = 5;
        } else {
          // Перевіряємо чи є фонова сесія з таким самим часом початку
          const backgroundSessionAtSameTime = day.registeredSessions.find(s => {
            if (!s.isBackground) return false;
            const bgStart = new Date(s.start_at);
            return Math.abs(bgStart.getTime() - sessionStart.getTime()) < 1000;
          });

          const overlappingInfo = this.getOverlappingSessionsInfo(session, day.registeredSessions);

          // Якщо є фонова сесія з таким самим часом, зміщуємо регулярні сесії вправо
          if (backgroundSessionAtSameTime) {
            const columnWidth = 100 / (overlappingInfo.total + 1); // +1 для фонової
            const leftPercent = columnWidth + (overlappingInfo.position * columnWidth);

            leftStyle = `${leftPercent}%`;
            widthStyle = `${columnWidth}%`;
          } else {
            const columnWidth = 100 / overlappingInfo.total;
            const leftPercent = overlappingInfo.position * columnWidth;

            leftStyle = `${leftPercent}%`;
            widthStyle = `${columnWidth}%`;
          }
          zIndex = 10 + currentColumn;
        }
      } else {
        // Desktop
        if (isBackground) {
          leftStyle = '0%';
          widthStyle = '100%';
          zIndex = 5;
        } else {
          // Перевіряємо чи є фонова сесія з таким самим часом початку
          const backgroundSessionAtSameTime = day.registeredSessions.find(s => {
            if (!s.isBackground) return false;
            const bgStart = new Date(s.start_at);
            return Math.abs(bgStart.getTime() - sessionStart.getTime()) < 1000;
          });

          const overlappingInfo = this.getOverlappingSessionsInfo(session, day.registeredSessions);

          if (overlappingInfo.total > 0) {
            // Якщо є фонова сесія з таким самим часом, зміщуємо регулярні сесії вправо
            if (backgroundSessionAtSameTime) {
              const totalWithBackground = overlappingInfo.total + 1; // +1 для місця фонової сесії
              const columnWidth = 100 / totalWithBackground;
              const expandFactor = 1.3;

              // Залишаємо перший слот для фонової сесії, регулярні починаються з другого
              const leftPercent = columnWidth + (overlappingInfo.position * columnWidth);
              const widthPercent = Math.min(columnWidth * expandFactor, 100 - leftPercent);

              leftStyle = `${leftPercent}%`;
              widthStyle = `${widthPercent}%`;
            } else {
              const columnWidth = 100 / overlappingInfo.total;
              const expandFactor = 1.5;

              const leftPercent = overlappingInfo.position * columnWidth;
              const widthPercent = Math.min(columnWidth * expandFactor, 100 - leftPercent);

              leftStyle = `${leftPercent}%`;
              widthStyle = `${widthPercent}%`;
            }
          } else {
            const totalColumns = session.totalColumns || 1;
            const columnWidth = 100 / totalColumns;
            const expandFactor = 1.7;

            const leftPercent = currentColumn * columnWidth;
            const widthPercent = Math.min(columnWidth * expandFactor, 100 - leftPercent);

            leftStyle = `${leftPercent}%`;
            widthStyle = `calc(${widthPercent}% - 2px)`;
          }
          zIndex = 10 + currentColumn;
        }
      }

      return {
        top: `${topPx}px`,
        height: `${Math.max(heightPx, 30)}px`,
        left: leftStyle,
        width: widthStyle,
        right: 'auto',
        background: session.is_educational
          ? 'linear-gradient(135deg, var(--attendee-brand-color) 0%, var(--attendee-brand-color) 100%)'
          : 'linear-gradient(135deg, #7B68EE 0%, #7B68EE 100%)',
        zIndex: zIndex,
      };
    },

    getGapStyle(gap, day) {
      const firstHour = day.hours[0];
      const gridStartTime = new Date(day.startTime);
      gridStartTime.setHours(firstHour, 0, 0, 0);

      const gridStartMs = gridStartTime.getTime();

      const startOffsetMs = gap.start.getTime() - gridStartMs;
      const durationMs = gap.end.getTime() - gap.start.getTime();

      const startOffsetHours = startOffsetMs / (1000 * 60 * 60);
      const durationHours = durationMs / (1000 * 60 * 60);

      const topPx = startOffsetHours * 60;
      const heightPx = durationHours * 60;

      return {
        top: `${topPx}px`,
        height: `${Math.max(heightPx, 30)}px`
      };
    },

    formatHour(hour) {
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour} ${period}`;
    },

    formatTime(date) {
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      const displayMinutes = minutes.toString().padStart(2, '0');
      return `${displayHour}:${displayMinutes} ${period}`;
    },

    openGapModal(gap, day) {
      this.selectedGap = {
        ...gap,
        timeDisplay: `${this.formatTime(gap.start)} - ${this.formatTime(gap.end)}`
      };
      this.selectedDay = day;

      this.availableSessions = day.sessions.filter(session => {
        if (session.my_agenda === true) return false;
        if (!session.buttons?.my_schedule?.enabled) return false;

        const sessionStart = new Date(session.start_at);
        const sessionEnd = new Date(session.end_at);

        return sessionStart >= gap.start && sessionEnd <= gap.end;
      });

      this.showGapModal = true;
    },

    closeGapModal() {
      this.showGapModal = false;
      this.selectedGap = null;
      this.selectedDay = null;
      this.availableSessions = [];
    },

    handleSessionUpdated(session) {
      // Modal component already handles store updates
    },

    openSessionDetails(session) {
      this.$emit('open-session', session);
    },

    _onScroll() {
      this._requestScrollMeasure();
    },

    _requestScrollMeasure() {
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = 0;
        this.updateStickyDayBar();
      });
    },

    initDayObserver() {
      const topOffset = this.calculatedStickyTop;
      this._dayIO = new IntersectionObserver(
        this.onDayIntersect,
        {
          root: null,
          rootMargin: `-${topOffset}px 0px -85% 0px`,
          threshold: 0,
        }
      );
    },

    observeDayHeaders() {
      const container = document.querySelector('.calendar-timeline');
      if (!container) return;

      const headers = container.querySelectorAll('.calendar-day-title');
      this._observedDayHeaders = Array.from(headers);

      if (!this._observedDayHeaders || !this._observedDayHeaders.length) {
        return;
      }

      this._observedDayHeaders.forEach((el) => {
        if (!el.dataset.dayLabel) {
          const h3 = el.querySelector('h3');
          el.dataset.dayLabel = h3 ? h3.textContent.trim() : '';
        }
        this._dayIO.observe(el);
      });
    },

    disconnectDayObserver() {
      if (this._dayIO) {
        if (this._observedDayHeaders && this._observedDayHeaders.length) {
          this._observedDayHeaders.forEach((el) => this._dayIO.unobserve(el));
        }
        this._dayIO.disconnect();
        this._dayIO = null;
      }
      this._observedDayHeaders = [];
    },

    onDayIntersect(entries) {
      let latestLabel = null;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          latestLabel = entry.target.dataset.dayLabel || '';
        }
      });

      if (latestLabel) {
        this.currentStickyDay = latestLabel;
        return;
      }

      const topOffset = this.calculatedStickyTop;
      const headers = this._observedDayHeaders;
      if (!headers.length) {
        this.currentStickyDay = '';
        return;
      }

      let candidate = '';
      for (const el of headers) {
        const rect = el.getBoundingClientRect();
        if (rect.top - topOffset <= 0) {
          candidate = el.dataset.dayLabel || '';
        } else {
          break;
        }
      }
      if (!candidate) {
        candidate = headers[0].dataset.dayLabel || '';
      }
      this.currentStickyDay = candidate;
    },

    updateStickyDayBar() {
      const container = document.querySelector('.calendar-timeline');
      if (!container) return;

      const headers = Array.from(container.querySelectorAll('.calendar-day-title'));
      if (!headers.length) {
        this.currentStickyDay = '';
        return;
      }

      const topOffset = this.calculatedStickyTop;
      const stickyHeaderHeight = 60;

      let activeIdx = 0;
      for (let i = 0; i < headers.length; i++) {
        const rect = headers[i].getBoundingClientRect();
        if (rect.top <= topOffset + stickyHeaderHeight) {
          activeIdx = i;
        } else {
          break;
        }
      }

      const activeHeader = headers[activeIdx];
      const titleEl = activeHeader?.querySelector('h3');
      const title = titleEl ? titleEl.textContent.trim() : '';

      if (this.currentStickyDay !== title) {
        this.currentStickyDay = title;
      }
    },

    rebuildDayObserver() {
      this.disconnectDayObserver();

      this.$nextTick(() => {
        this.initDayObserver();
        this.observeDayHeaders();

        if (this.calendarDays.length > 0) {
          this.currentStickyDay = this.calendarDays[0].date_long;
        }
      });
    },

    isBottomHalf(session, day) {
      if (!day.hours || day.hours.length === 0) return false;

      const startHour = day.hours[0];
      const totalHours = day.hours.length;
      const sessionStart = new Date(session.start_at).getHours();

      return (sessionStart - startHour) > (totalHours / 2);
    },

    isRightSide(session, day) {
      if (session.isBackground) return false;

      const overlappingInfo = this.getOverlappingSessionsInfo(session, day.registeredSessions);
      if (overlappingInfo.total <= 1) return false;

      return overlappingInfo.position >= overlappingInfo.total / 2;
    },

    getOverlappingSessionsCount(currentSession, allSessions) {
      const currentStart = new Date(currentSession.start_at);
      const currentEnd = new Date(currentSession.end_at);

      let maxOverlap = 1;

      const checkPoints = 10;
      const duration = currentEnd - currentStart;

      for (let i = 0; i <= checkPoints; i++) {
        const checkTime = new Date(currentStart.getTime() + (duration * i / checkPoints));
        let overlapCount = 0;

        allSessions.forEach(session => {
          const sessionStart = new Date(session.start_at);
          const sessionEnd = new Date(session.end_at);

          if (sessionStart <= checkTime && sessionEnd > checkTime) {
            overlapCount++;
          }
        });

        maxOverlap = Math.max(maxOverlap, overlapCount);
      }

      return maxOverlap;
    },

    getOverlappingSessionsInfo(currentSession, allSessions) {
      if (currentSession.isBackground) {
        return { total: 1, position: 0 };
      }

      const currentStart = new Date(currentSession.start_at);
      const currentEnd = new Date(currentSession.end_at);

      const overlapping = allSessions.filter(session => {
        if (session.isBackground) return false;

        const sessionStart = new Date(session.start_at);
        const sessionEnd = new Date(session.end_at);

        return sessionStart < currentEnd && sessionEnd > currentStart;
      });

      overlapping.sort((a, b) => {
        const startDiff = new Date(a.start_at) - new Date(b.start_at);
        if (startDiff !== 0) return startDiff;
        return (a.column || 0) - (b.column || 0);
      });

      const position = overlapping.findIndex(s => s.id === currentSession.id);

      return {
        total: overlapping.length,
        position: position >= 0 ? position : 0
      };
    },
  }
}
</script>

<style scoped lang="scss">
.calendar-wrapper {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: var(--attendee-gap-lg);
  max-width: 100%;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    margin-top: 0;
  }
}

.view-toggle {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  padding: 0 var(--attendee-gap-md);

  @media (max-width: 900px) {
    padding: var(--attendee-gap-md);
  }
}

.calendar-timeline {
  @media (max-width: 900px) {
    padding: var(--attendee-gap-md);
    padding-top: 0;
  }
}

.calendar-day-section {
  margin-bottom: 40px;
}

.calendar-day-section.first-day .calendar-day-title {
  visibility: hidden;
  height: 0 !important;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  overflow: hidden;
  line-height: 0;
}

.calendar-sticky-header {
  position: sticky;
  top: var(--calendar-sticky-top, 80px);
  z-index: 21;
  background: white;
  margin: 0 calc(-1 * var(--attendee-gap-md)) 20px;
  padding: var(--attendee-gap-md);
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 900px) {
    padding: 5px var(--attendee-gap-md) !important;
  }
}

.calendar-sticky-header__left {
  display: flex;
  align-items: center;
  gap: var(--attendee-gap-md);

  h3 {
    margin: 0 !important;
    font-size: 21px;
    color: var(--attendee-text-primary-color);

    @media (max-width: 900px) {
      font-size: 16px !important;
      text-align: left !important;
      padding: 0 !important;
    }
  }
}

.calendar-day-title {
  margin: 0 calc(-1 * var(--attendee-gap-md)) 20px;
  padding: var(--attendee-gap-md);

  h3 {
    margin: 0 !important;
    font-size: 21px;
    color: var(--attendee-text-primary-color);

    @media (max-width: 900px) {
      font-size: 16px !important;
    }
  }

  @media (max-width: 900px) {
    padding: var(--attendee-gap-sm) var(--attendee-gap-md);
  }
}

.timeline-container {
  display: flex;
  gap: 10px;
  min-height: 400px;
}

.time-labels {
  width: 40px;
  flex-shrink: 0;
  position: relative;
}

.time-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  height: 60px;
  display: flex;
  align-items: center;
  line-height: 1;
  position: relative;
  top: -30px;
}

.timeline-grid {
  flex: 1;
  position: relative;
  background: #f3f4f6;
  border-radius: 8px;
  min-height: 100%;
  overflow: visible;
}

.hour-line {
  position: absolute;
  left: -5px;
  right: 0;
  height: 1px;
  background-color: #d1d5db;
  pointer-events: none;
  z-index: 1;

  &:first-child {
    background-color: transparent;
  }
}

.session-block {
  position: absolute;
  border: 1px solid #fff;
  border-radius: 8px;
  padding: 6px;
  cursor: pointer;
  box-shadow: none;
  z-index: 10;

  @media (min-width: 901px) {
    transition: all 0.2s ease;

    &:hover:not(.background-session) {
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
      z-index: 100 !important;
    }
  }

  @media (max-width: 900px) {
    padding: 4px;
  }
}

.session-block-content {
  color: white;
  font-size: 12px;
  overflow: hidden;
  height: 100%;
}

.session-name {
  font-weight: 600;
  margin-bottom: 0;
  line-height: 1.3;
  font-size: 13px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-wrap: break-word;
  word-break: break-word;

  @media (max-width: 900px) {
    font-size: 10px;
    line-height: 1.25;
    -webkit-line-clamp: 3;
  }
}

.gap-block {
  position: absolute;
  left: 0;
  right: 0;
  width: 100%;
  background: rgba(249, 115, 22, 0.1);
  border: 2px dashed #f97316;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  box-sizing: border-box;

  &:hover {
    background: rgba(249, 115, 22, 0.2);
    border-color: #ea580c;
    z-index: 6;
  }
}

.gap-content {
  text-align: center;
  color: #ea580c;
  font-weight: 600;
  font-size: 13px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.gap-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  i {
    font-size: 16px;
  }

  span {
    white-space: nowrap;
  }
}

.gap-time {
  font-size: 11px;
  opacity: 0.8;
  font-weight: 500;
}

@media (max-width: 900px) {
  .timeline-container {
    gap: 5px;
  }

  .time-labels {
    width: 40px;
  }

  .time-label {
    font-size: 11px;
  }

  .gap-block {
    left: 0;
    right: 0;
  }
}

/* Tooltip styles - Desktop only */
@media (min-width: 901px) {
  .session-tooltip {
    position: absolute;
    left: 0;
    top: 100%;
    width: 100%;
    min-width: 200px;
    background: white;
    color: #333;
    padding: 12px;
    border-radius: 4px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.2);
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease, transform 0.2s ease;
    transform: translateY(-5px);
    pointer-events: none;
    margin-top: 5px;
    border: 1px solid #eee;
    z-index: 101;
  }

  .session-tooltip.tooltip-top {
    top: auto;
    bottom: 100%;
    margin-top: 0;
    margin-bottom: 5px;
    transform: translateY(5px);
  }

  .session-tooltip.tooltip-right {
    left: auto;
    right: 0;
  }

  .session-block:hover:not(.background-session) .session-tooltip {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0);
  }

  .tooltip-header {
    margin-bottom: 5px;
    border-bottom: 1px solid #eee;
    padding-bottom: 5px;
  }

  .tooltip-time {
    font-size: 11px;
    font-weight: 600;
    color: #666;
  }

  .tooltip-title {
    font-weight: bold;
    font-size: 13px;
    line-height: 1.4;
    color: #2c3e50;
    margin-bottom: 5px;
  }

  .tooltip-footer {
    font-size: 11px;
    color: #888;
    display: flex;
    align-items: center;
    gap: 5px;
  }
}

/* Hide tooltips completely on mobile */
@media (max-width: 900px) {
  .session-tooltip {
    display: none !important;
  }
}
</style>
